import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { LoginActivity } from "@/components/LoginActivity";
import { MessageSquare, CheckCircle2, XCircle, CalendarDays, DollarSign, Users, Save, Loader2, Trash2, Database, Mail, KeyRound, Copy, Check } from "lucide-react";

function Stat({ icon: Icon, label, value, tone = "default", testid }) {
  const tones = { default: "text-foreground", success: "text-[hsl(var(--success))]", destructive: "text-destructive", primary: "text-primary" };
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="uppercase text-xs tracking-[0.2em] text-muted-foreground">{label}</p>
        <Icon className={`h-5 w-5 ${tones[tone]}`} />
      </div>
      <p className={`font-head text-3xl font-800 mt-3 tracking-tight ${tones[tone]}`}>{value}</p>
    </Card>
  );
}

export default function AdminPage() {
  const [data, setData] = useState(null);
  const [rate, setRate] = useState("0.05");
  const [currency, setCurrency] = useState("AUD");
  const [savingCost, setSavingCost] = useState(false);

  const [users, setUsers] = useState([]);
  const [emails, setEmails] = useState({}); // id -> edited email
  const [savingEmail, setSavingEmail] = useState(null);
  const [resetResult, setResetResult] = useState(null); // {email, temporary_password}
  const [copied, setCopied] = useState(false);

  const loadUsers = useCallback(() => api.get("/admin/users").then((r) => {
    setUsers(r.data);
    setEmails(Object.fromEntries(r.data.map((u) => [u.id, u.email])));
  }), []);

  const load = useCallback(() => api.get("/admin/overview").then((r) => {
    setData(r.data);
    setRate(String(r.data.cost_per_sms));
    setCurrency(r.data.currency);
  }), []);
  useEffect(() => { load(); loadUsers(); }, [load, loadUsers]);

  const saveEmail = async (u) => {
    setSavingEmail(u.id);
    try {
      await api.put(`/admin/users/${u.id}/email`, { email: emails[u.id] });
      toast.success("Login email updated");
      loadUsers();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingEmail(null); }
  };

  const resetPassword = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setResetResult(data);
      setCopied(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const copyTemp = () => {
    navigator.clipboard?.writeText(resetResult.temporary_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const saveCost = async () => {
    setSavingCost(true);
    try {
      await api.put("/admin/cost", { cost_per_sms: parseFloat(rate), currency });
      toast.success("Cost settings saved");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingCost(false); }
  };

  const clearLogs = async () => {
    try { const { data } = await api.delete("/admin/logs"); toast.success(`${data.deleted} message logs cleared`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const money = (v) => `${data?.currency === "AUD" ? "$" : ""}${Number(v).toFixed(2)}${data?.currency !== "AUD" ? " " + data?.currency : ""}`;

  return (
    <div className="animate-fade-up" data-testid="admin-page">
      <p className="uppercase text-xs tracking-[0.3em] text-muted-foreground mb-2">Admin</p>
      <h1 className="font-head text-4xl sm:text-5xl font-800 tracking-tight leading-none">Control Centre</h1>
      <p className="text-muted-foreground mt-3 mb-8">Text volume, running costs, and data management.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat icon={MessageSquare} label="Total texts" value={data?.total_messages ?? "–"} tone="primary" testid="admin-stat-total" />
        <Stat icon={CheckCircle2} label="Delivered" value={data?.delivered ?? "–"} tone="success" testid="admin-stat-delivered" />
        <Stat icon={XCircle} label="Undelivered" value={data?.undelivered ?? "–"} tone="destructive" testid="admin-stat-undelivered" />
        <Stat icon={CalendarDays} label="Texts this month" value={data?.month_count ?? "–"} testid="admin-stat-month" />
        <Stat icon={DollarSign} label="Est. spend (all time)" value={data ? money(data.estimated_spend) : "–"} tone="primary" testid="admin-stat-spend" />
        <Stat icon={DollarSign} label="Spend this month" value={data ? money(data.month_spend) : "–"} testid="admin-stat-month-spend" />
        <Stat icon={Users} label="Staff / available" value={data ? `${data.staff_total} / ${data.staff_available}` : "–"} testid="admin-stat-staff" />
        <Stat icon={CalendarDays} label="Weekly shifts" value={data?.shifts_total ?? "–"} testid="admin-stat-shifts" />
        <Stat icon={Database} label="Holiday / closure days" value={data?.exceptions_total ?? "–"} testid="admin-stat-exceptions" />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-8">
        <Card className="p-6">
          <h3 className="font-head text-lg font-700 mb-1">Cost variables</h3>
          <p className="text-sm text-muted-foreground mb-5">Set your Twilio per-SMS rate — spend is estimated from this. Update once you know your real rate.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Cost per SMS</Label>
              <Input type="number" step="0.001" min="0" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="cost-rate-input" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Currency</Label>
              <Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} data-testid="cost-currency-input" className="mt-1.5" maxLength={3} />
            </div>
          </div>
          <Button onClick={saveCost} disabled={savingCost} variant="secondary" data-testid="save-cost-button" className="gap-2 mt-4">
            {savingCost ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save cost settings
          </Button>
          <div className="mt-5 rounded-md bg-muted/50 border border-border px-3 py-2 text-sm" data-testid="cost-summary">
            {data ? <>Twilio: <span className="font-medium">{data.twilio_enabled ? "Live" : "Simulation"}</span> · Automation: <span className="font-medium">{data.automation_enabled ? "On" : "Off"}</span> · Send time <span className="font-medium">{data.default_send_time}</span></> : "…"}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-head text-lg font-700 mb-1">Data management</h3>
          <p className="text-sm text-muted-foreground mb-5">Clear historical data. This cannot be undone.</p>
          <div className="space-y-3">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="w-full justify-start gap-2 text-destructive" data-testid="clear-logs-button"><Trash2 className="h-4 w-4" /> Clear all message logs</Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="clear-logs-confirm">
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear all message logs?</AlertDialogTitle>
                  <AlertDialogDescription>This permanently deletes every sent-text record and resets the delivery summary.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={clearLogs} data-testid="confirm-clear-logs">Clear logs</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </Card>
      </div>

      {/* Login management */}
      <Card className="p-6 mt-4" data-testid="login-management">
        <h3 className="font-head text-lg font-700 mb-1">Login accounts</h3>
        <p className="text-sm text-muted-foreground mb-5">Change a login's email or issue a temporary password to hand over.</p>
        <div className="space-y-4">
          {users.map((u) => (
            <div key={u.id} className="rounded-md border border-border p-4" data-testid={`user-row-${u.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                  {u.role === "admin" ? "Admin" : "Company"}
                </span>
                {u.mfa_enabled && <span className="text-[10px] uppercase tracking-widest text-muted-foreground">MFA on</span>}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                <div className="flex-1">
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Login email</Label>
                  <Input value={emails[u.id] ?? ""} onChange={(e) => setEmails({ ...emails, [u.id]: e.target.value })} data-testid={`email-input-${u.id}`} className="mt-1.5" />
                </div>
                <Button onClick={() => saveEmail(u)} disabled={savingEmail === u.id || (emails[u.id] || "").toLowerCase() === u.email} variant="secondary" data-testid={`save-email-${u.id}`} className="gap-2">
                  {savingEmail === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save email
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" data-testid={`reset-pw-${u.id}`} className="gap-2"><KeyRound className="h-4 w-4" /> Reset password</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid={`reset-pw-confirm-${u.id}`}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Reset password for {u.email}?</AlertDialogTitle>
                      <AlertDialogDescription>A new temporary password will be generated and shown once. The existing password stops working immediately — share the new one with the account holder so they can sign in and change it.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => resetPassword(u)} data-testid={`confirm-reset-pw-${u.id}`}>Generate password</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <LoginActivity />

      {/* Temp password reveal */}
      <AlertDialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}>
        <AlertDialogContent data-testid="temp-password-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Temporary password</AlertDialogTitle>
            <AlertDialogDescription>
              Give this to <strong>{resetResult?.email}</strong>. It won't be shown again. Ask them to sign in and change it under Account → Password.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-center justify-between gap-3 rounded-md bg-muted border border-border px-3 py-2.5 font-mono text-sm" data-testid="temp-password-value">
            <span className="break-all">{resetResult?.temporary_password}</span>
            <Button size="icon" variant="ghost" onClick={copyTemp} data-testid="copy-temp-password">
              {copied ? <Check className="h-4 w-4 text-[hsl(var(--success))]" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setResetResult(null)}>Done</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
